import type {
  BasicDataQuery,
  BillingEntitiesDataQuery,
  BrandsDataQuery,
  BudgetCategory,
  OutgoingInvoiceQuery,
  ProformaInvoiceQuery,
  ProjectsDataQuery,
  ProjectsSimpleQuery,
  RequestForInvoicingQuery,
  UnusedPaidProformaInvoicesQuery,
} from "@src/__generated__/graphql";
import type { InvoiceStore } from "@src/components/modules/invoices/Create/store";
import { can } from "@src/utils/components/permissions";
import { sumBy } from "lodash";
import { action, computed, makeObservable } from "mobx";

export type ClientsSimple = BasicDataQuery["clientsSimpleMap"];
export type UsersSimple = BasicDataQuery["userSimpleMap"];
export type TeamSimple = BasicDataQuery["teamSimpleMap"];

export type VatRates = NonNullable<BasicDataQuery["vatRates"]>;
export type Brands = NonNullable<BrandsDataQuery["brands"]>["data"];
export type BillingEntities = NonNullable<
  BillingEntitiesDataQuery["client"]
>["billingEntities"];
export type Projects = NonNullable<ProjectsDataQuery["projects"]>["data"];
export type ProjectsSimple = ProjectsSimpleQuery["projectsSimpleMap"];
export type ProformaInvoices =
  UnusedPaidProformaInvoicesQuery["unusedPaidOutgoingProformaInvoices"];

type TOurWorkStructured = Projects[0]["ourWorkStructured"][0];
export type TOurWorkBudgetItem = TOurWorkStructured["budgetItems"][0];
type TExpenseWithoutOtherStructured =
  Projects[0]["expensesWithoutOtherStructured"][0];
export type TExpenseBudgetItem =
  TExpenseWithoutOtherStructured["budgetItems"][0];

export type RequestForInvoicingData = NonNullable<
  RequestForInvoicingQuery["requestForInvoicing"]
>;
export type OutgoingInvoiceData = NonNullable<
  OutgoingInvoiceQuery["outgoingInvoice"]
>;
export type ProformaInvoiceData = NonNullable<
  ProformaInvoiceQuery["outgoingProformaInvoice"]
>;

export class ProjectModel {
  invoiceStore: InvoiceStore;
  id: Projects[0]["id"];
  title: Projects[0]["title"];
  code: Projects[0]["code"];
  finished_at: Projects[0]["finished_at"];
  archived_at: Projects[0]["archived_at"];
  ourWorkStructured: OurWorkStructuredModel[];
  expensesWithoutOtherStructured: ExpenseWithoutOtherStructuredModel[];

  constructor(src: Projects[0], invoiceStore: InvoiceStore) {
    makeObservable(this);
    this.invoiceStore = invoiceStore;
    this.id = src.id;
    this.title = src.title;
    this.code = src.code;
    this.finished_at = src.finished_at;
    this.archived_at = src.archived_at;
    this.ourWorkStructured = src.ourWorkStructured.map(
      (ourWork) => new OurWorkStructuredModel(ourWork, this.invoiceStore),
    );
    this.expensesWithoutOtherStructured = can([
      "expense_read_all",
      "expense_read_team",
      "expense_read_own",
    ])
      ? src.expensesWithoutOtherStructured.map(
          (item) =>
            new ExpenseWithoutOtherStructuredModel(item, this.invoiceStore),
        )
      : [];
  }

  @computed get itemsLength(): number {
    return (
      sumBy(this.ourWorkStructured, (ourWork) => ourWork.budgetItems.length) +
      sumBy(
        this.expensesWithoutOtherStructured,
        (item) => item.budgetItems.length,
      )
    );
  }

  @computed get selectedCount(): number {
    return (
      sumBy(this.expensesWithoutOtherStructured, (item) => item.selectedCount) +
      sumBy(this.ourWorkStructured, (item) => item.selectedCount)
    );
  }

  @computed get allSelected(): boolean {
    return this.itemsLength === this.selectedCount;
  }

  @action.bound toggleSelectAll(): void {
    const checked = this.allSelected ? false : true;

    this.ourWorkStructured.map((item) =>
      item.toggleSelectAll(checked, item.budget_category_id),
    );
    this.expensesWithoutOtherStructured.map((item) =>
      item.toggleSelectAll(checked, item.budget_category_id),
    );
  }
}

type StructuredBaseSource = {
  id: string;
  name: string;
  total_budget: number;
  budget_category_id: string;
};

class StructuredModelBase implements StructuredBaseSource {
  invoiceStore: InvoiceStore;
  id;
  budget_category_id;
  name;
  total_budget;

  constructor(src: StructuredBaseSource, invoiceStore: InvoiceStore) {
    makeObservable(this);
    this.invoiceStore = invoiceStore;
    this.id = src.id;
    this.name = src.name;
    this.total_budget = src.total_budget;
    this.budget_category_id = src.budget_category_id;
  }
}

export class OurWorkStructuredModel extends StructuredModelBase {
  _budgetItems: TOurWorkStructured["budgetItems"];

  constructor(src: TOurWorkStructured, invoiceStore: InvoiceStore) {
    super(src, invoiceStore);
    makeObservable(this);
    this._budgetItems = src.budgetItems;
  }

  @computed get budgetItems(): TOurWorkStructured["budgetItems"] {
    return this._budgetItems.filter((bi) =>
      this.invoiceStore.showBudgetItem(bi),
    );
  }

  @computed get ids(): string[] {
    return this.budgetItems.map((bi) => bi.id);
  }

  @computed get selectedCount(): number {
    return this.ids.filter((id) => this.ourWorkSplitIds.includes(id)).length;
  }

  @computed get ourWorkSplitIds(): string[] {
    return Array.from(this.invoiceStore.form.our_work_budget_splits.$.keys());
  }

  @computed get allSelected(): boolean {
    return this.selectedCount === this.ids.length;
  }

  @action.bound toggleSelectAll(
    checked: boolean | undefined = undefined,
    budgetCategoryId: BudgetCategory["id"],
  ): void {
    const _checked =
      checked !== undefined ? checked : this.allSelected ? false : true;

    this.budgetItems.forEach((item) => {
      this.invoiceStore.toggleCheckOurWorkItem(
        item,
        _checked,
        budgetCategoryId,
      );
    });
  }
}

export class ExpenseWithoutOtherStructuredModel extends StructuredModelBase {
  _budgetItems: TExpenseWithoutOtherStructured["budgetItems"];

  constructor(src: TExpenseWithoutOtherStructured, invoiceStore: InvoiceStore) {
    super(src, invoiceStore);
    makeObservable(this);
    this._budgetItems = src.budgetItems;
  }

  @computed get budgetItems(): TExpenseWithoutOtherStructured["budgetItems"] {
    return this._budgetItems.filter((bi) =>
      this.invoiceStore.showBudgetItem(bi),
    );
  }

  @computed get ids(): string[] {
    return this.budgetItems.map((bi) => bi.id);
  }

  @computed get selectedCount(): number {
    return this.ids.filter((id) => this.expenseSplitIds.includes(id)).length;
  }

  @computed get expenseSplitIds(): string[] {
    return Array.from(this.invoiceStore.form.expense_budget_splits.$.keys());
  }

  @computed get allSelected(): boolean {
    return this.selectedCount === this.ids.length;
  }

  @action.bound toggleSelectAll(
    checked: boolean | undefined,
    budgetCategoryId: BudgetCategory["id"],
  ): void {
    const _checked =
      checked !== undefined ? checked : this.allSelected ? false : true;

    this.budgetItems.forEach((item) => {
      this.invoiceStore.toggleCheckExpenseItem(
        item,
        _checked,
        budgetCategoryId,
      );
    });
  }
}

export const isTOurWorkBudgetItem = (
  item: TOurWorkBudgetItem | TExpenseBudgetItem,
): item is TOurWorkBudgetItem => {
  return (item as TExpenseBudgetItem)?.stats.real_expenses === undefined;
};
